/* assets/testimonial-carousel.js
   ------------------------------------------------------------
   Requires jQuery + Slick Carousel already loaded on the page
   (project convention — see homepage-banner-slider.liquid).

   Responsibilities:
     1. Init the main carousel with variableWidth so the visible
        card count is fluid rather than breakpoint-based.
     2. Init one nested Slick instance per card that has 2+ linked
        tiles (single-tile cards are rendered as a plain <a>, no
        Slick, so arrows/dots never need to be manually hidden).
     3. Wire up in-frame video play/pause + mute controls. Video
        sources are attached on first play only (no preload).
     4. Guard nested-carousel links against firing a click at the
        end of a drag (a well-known Slick behaviour).
------------------------------------------------------------- */

(function (window, $) {
  'use strict';

  if (!$) {
    console.warn('[testimonial-carousel] jQuery not found — carousel not initialised.');
    return;
  }

  const instances = {};

  // Sources are rendered as data-tc-src so nothing downloads before play
  // (including Slick's cloned slides). Attach them on first play only.
  function ensureVideoSource(video) {
    if (video.dataset.tcSrcLoaded) return;
    video.dataset.tcSrcLoaded = 'true';
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    video.querySelectorAll('source[data-tc-src]').forEach(function (source) {
      source.src = (isMobile && source.dataset.tcSrcMobile) || source.dataset.tcSrc;
      source.removeAttribute('data-tc-src');
      source.removeAttribute('data-tc-src-mobile');
    });
    video.preload = 'auto';
    video.load();
  }

  function playVideo(video, root) {
    ensureVideoSource(video);
    // Only one testimonial video plays at a time.
    root.querySelectorAll('video.tc-video__el').forEach(function (other) {
      if (other !== video && !other.paused) other.pause();
    });
    const playPromise = video.play();
    if (playPromise && playPromise.catch) playPromise.catch(function () {});
  }

  function initVideoControls(root) {
    root.querySelectorAll('[data-tc-video]').forEach(function (wrap) {
      const video = wrap.querySelector('.tc-video__el');
      const playBtn = wrap.querySelector('[data-tc-video-play]');
      const pauseBtn = wrap.querySelector('[data-tc-video-pause]');
      const muteBtn = wrap.querySelector('[data-tc-video-mute]');
      if (!video || wrap.dataset.tcVideoBound) return;
      wrap.dataset.tcVideoBound = 'true';

      function showPlay() {
        if (playBtn) playBtn.hidden = false;
        if (pauseBtn) pauseBtn.setAttribute('aria-hidden', 'true');
        wrap.classList.remove('is-playing');
      }
      function hidePlay() {
        if (playBtn) playBtn.hidden = true;
        if (pauseBtn) pauseBtn.removeAttribute('aria-hidden');
        wrap.classList.add('is-playing');
      }

      if (playBtn) {
        playBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          playVideo(video, root);
          hidePlay();
          if (muteBtn) muteBtn.hidden = false;
        });
      }

      if (pauseBtn) {
        pauseBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          video.pause();
        });
      }

      video.addEventListener('click', function () {
        if (video.paused) {
          playVideo(video, root);
        } else {
          video.pause();
        }
      });

      video.addEventListener('play', hidePlay);
      video.addEventListener('pause', showPlay);
      video.addEventListener('ended', showPlay);

      if (muteBtn) {
        muteBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          video.muted = !video.muted;
          muteBtn.classList.toggle('is-muted', video.muted);
        });
      }
    });
  }

  function guardNestedLinkClicks($nested) {
    let dragging = false;
    $nested
      .on('mousedown touchstart', function () {
        dragging = false;
      })
      .on('mousemove touchmove', function () {
        dragging = true;
      })
      .on('click', 'a', function (e) {
        if (dragging) {
          e.preventDefault();
          e.stopPropagation();
        }
      });
  }

  function initNestedCarousels(root) {
    const nestedEls = root.querySelectorAll('[data-tc-nested]');
    nestedEls.forEach(function (el) {
      const $el = $(el);
      if ($el.hasClass('slick-initialized')) return;

      const data = el.dataset;

      $el.slick({
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: toBool(data.arrows, true),
        prevArrow: PREV_ARROW,
        nextArrow: NEXT_ARROW,
        dots: toBool(data.dots, true),
        infinite: toBool(data.infinite, true),
        autoplay: toBool(data.autoplay, false),
        autoplaySpeed: toNumber(data.autoplaySpeed, 4000),
        speed: toNumber(data.speed, 300),
        adaptiveHeight: false,
        swipe: true,
        draggable: false, // mouse-drag reserved for the main carousel; nested uses arrows/dots on desktop
        responsive: [
          {
            breakpoint: 749,
            settings: {
              draggable: false,
              swipe: true
            }
          }
        ]
      });

      guardNestedLinkClicks($el);
    });
  }

  function toBool(value, fallback) {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function toNumber(value, fallback) {
    const num = parseInt(value, 10);
    return isNaN(num) ? fallback : num;
  }

  function arrowSvgPath(d) {
    return (
      '<svg xmlns="http://www.w3.org/2000/svg" width="30" height="13" viewBox="0 0 30 13" fill="none">' +
      '<path d="' +
      d +
      '" fill="currentColor" stroke="currentColor" stroke-width="0.2"></path>' +
      '</svg>'
    );
  }

  const PREV_ARROW_PATH =
    'M29.1 7.06237V7.16237H29L3.3838 7.16237L7.33817 11.1343L7.40843 11.2048L7.33816 11.2754L6.54625 12.0706L6.47538 12.1417L6.40452 12.0706L0.929131 6.57055L0.858895 6.5L0.929131 6.42945L6.40452 0.929448L6.47538 0.858274L6.54625 0.929436L7.33816 1.72463L7.40843 1.79518L7.33817 1.86575L3.3838 5.83763L29 5.83763H29.1V5.93763V7.06237Z';

  const NEXT_ARROW_PATH =
    'M0.9 5.93763V5.83763H1H26.6162L22.6618 1.86575L22.5916 1.79518L22.6618 1.72463L23.4538 0.929436L23.5246 0.858273L23.5955 0.929448L29.0709 6.42945L29.1411 6.5L29.0709 6.57055L23.5955 12.0706L23.5246 12.1417L23.4538 12.0706L22.6618 11.2754L22.5916 11.2048L22.6618 11.1343L26.6162 7.16237H1H0.9V7.06237V5.93763Z';

  const PREV_ARROW =
    '<button type="button" class="tc-arrow tc-arrow--prev" aria-label="Previous review">' +
    arrowSvgPath(PREV_ARROW_PATH) +
    '</button>';

  const NEXT_ARROW =
    '<button type="button" class="tc-arrow tc-arrow--next" aria-label="Next review">' +
    arrowSvgPath(NEXT_ARROW_PATH) +
    '</button>';

  function getFiniteMaxSlideIndex(slick) {
    const listWidth = slick.$list.outerWidth();
    const count = slick.slideCount;
    if (count <= 1) return 0;

    let totalWidth = 0;
    for (let i = 0; i < count; i++) {
      totalWidth += $(slick.$slides.get(i)).outerWidth(true);
    }

    const maxOffset = Math.max(0, totalWidth - listWidth);
    if (maxOffset === 0) return 0;

    let offset = 0;
    let maxIndex = 0;
    for (let i = 0; i < count; i++) {
      if (offset <= maxOffset + 0.5) maxIndex = i;
      else break;
      offset += $(slick.$slides.get(i)).outerWidth(true);
    }
    return maxIndex;
  }

  function isLastSlideFullyVisible(slick) {
    const last = slick.$slides.get(slick.slideCount - 1);
    const list = slick.$list.get(0);
    if (!last || !list) return true;
    const lastRect = last.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    return lastRect.right <= listRect.right + 1;
  }

  function syncFiniteMainEdges($track) {
    const slick = $track.slick('getSlick');
    if (!slick || slick.options.infinite) return;

    const maxIndex = getFiniteMaxSlideIndex(slick);
    const current = slick.currentSlide;
    const atEnd = current >= maxIndex || isLastSlideFullyVisible(slick);
    const $next = $track.children('.tc-arrow--next');
    const $prev = $track.children('.tc-arrow--prev');

    if (current > maxIndex) {
      $track.slick('slickGoTo', maxIndex, true);
      return;
    }

    $next.toggleClass('slick-disabled', atEnd).attr('aria-disabled', atEnd ? 'true' : 'false');
    $prev.toggleClass('slick-disabled', current <= 0).attr('aria-disabled', current <= 0 ? 'true' : 'false');
  }

  function bindFiniteMainEdges($track) {
    $track
      .off('.tcFinite')
      .on('setPosition.tcFinite afterChange.tcFinite breakpoint.tcFinite', function () {
        window.requestAnimationFrame(function () {
          syncFiniteMainEdges($track);
        });
      });

    const nextArrow = $track.children('.tc-arrow--next').get(0);
    if (nextArrow && !nextArrow.dataset.tcFiniteBound) {
      nextArrow.dataset.tcFiniteBound = 'true';
      nextArrow.addEventListener(
        'click',
        function (e) {
          const slick = $track.slick('getSlick');
          if (!slick || slick.options.infinite) return;
          const maxIndex = getFiniteMaxSlideIndex(slick);
          if (slick.currentSlide >= maxIndex || isLastSlideFullyVisible(slick)) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        },
        true
      );
    }
  }

  function initMainCarousel(sectionEl) {
    const trackEl = sectionEl.querySelector('.tc-track');
    if (!trackEl) return null;

    const $track = $(trackEl);
    if ($track.hasClass('slick-initialized')) return $track;

    const data = trackEl.dataset;
    const infinite = toBool(data.infinite, false);

    $track.slick({
      slidesToShow: 1,
      variableWidth: true,
      arrows: toBool(data.arrows, true),
      prevArrow: PREV_ARROW,
      nextArrow: NEXT_ARROW,
      dots: toBool(data.dots, false),
      infinite: infinite,
      autoplay: toBool(data.autoplay, false),
      autoplaySpeed: toNumber(data.autoplaySpeed, 4000),
      swipe: true,
      draggable: true,
      speed: toNumber(data.speed, 300)
    });

    if (!infinite) {
      bindFiniteMainEdges($track);
      syncFiniteMainEdges($track);
    }

    return $track;
  }

  const TestimonialCarousel = {
    init: function (sectionId) {
      const sectionEl = document.getElementById(sectionId);
      if (!sectionEl || sectionEl.dataset.tcInitialised) return;
      sectionEl.dataset.tcInitialised = 'true';

      const $main = initMainCarousel(sectionEl);
      initNestedCarousels(sectionEl);
      initVideoControls(sectionEl);

      instances[sectionId] = { $main: $main, el: sectionEl };
    },

    destroy: function (sectionId) {
      const instance = instances[sectionId];
      if (!instance) return;

      instance.el.querySelectorAll('[data-tc-nested].slick-initialized').forEach(function (el) {
        $(el).slick('unslick');
      });
      if (instance.$main && instance.$main.hasClass('slick-initialized')) {
        instance.$main.slick('unslick');
      }
      delete instance.el.dataset.tcInitialised;
      delete instances[sectionId];
    }
  };

  window.TestimonialCarousel = TestimonialCarousel;
})(window, window.jQuery);
